'use client';
import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { OrdersModal } from './orders-modal';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const pushCampaigns = [
  {
    id: 'push-001',
    name: 'New Feature Announcement',
    sent: 5250,
    delivered: 5120,
    message:
      '🎉 Exciting News! 🎉\n\nCheck out our brand new loyalty features and earn more rewards than ever before! 💎✨',
    revenue: '$3,780',
    optOutRate: '0.3%',
  },
  {
    id: 'push-002',
    name: 'Flash Sale Alert',
    sent: 4800,
    delivered: 4650,
    message:
      "⚡ FLASH SALE ALERT ⚡\n\n24-hour sale: 20% off ALL products!\n🔥 Don't miss out - ends midnight! 🔥",
    revenue: '$7,240',
    optOutRate: '0.2%',
  },
  {
    id: 'push-003',
    name: 'Limited Time Offer',
    sent: 3950,
    delivered: 3820,
    message:
      "✨ LIMITED EDITION DROP ✨\n\nExclusive products now available!\n🚀 Get yours before they're gone! 🚀",
    revenue: '$4,320',
    optOutRate: '0.4%',
  },
  {
    id: 'push-004',
    name: 'Abandoned Cart Reminder',
    sent: 2850,
    delivered: 2780,
    message:
      '🛒 Hey there! 👋\n\nYour cart is waiting for you!\n💰 Complete your purchase and save big! 💰',
    revenue: '$5,680',
    optOutRate: '0.1%',
  },
  {
    id: 'push-005',
    name: 'Event Reminder',
    sent: 3250,
    delivered: 3180,
    message:
      "📅 EVENT REMINDER 📅\n\nDon't forget our in-store event tomorrow!\n🎊 Special deals & prizes await! 🎊",
    revenue: '$2,150',
    optOutRate: '0.2%',
  },
];

interface PushCampaignsTableProps {
  timeRange: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function PushCampaignsTable({ timeRange }: PushCampaignsTableProps) {
  const [selectedCampaign, setSelectedCampaign] = useState<{
    name: string;
    revenue: string;
  } | null>(null);

  const handleRevenueClick = (campaign: { name: string; revenue: string }) => {
    setSelectedCampaign(campaign);
  };

  return (
    <div className='relative'>
      <div className='overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent -webkit-overflow-scrolling-touch'>
        <div className='min-w-[900px] p-6'>
          <Table>
            <TableHeader>
              <TableRow className='border-b hover:bg-transparent'>
                <TableHead className='w-[250px] bg-white sticky left-0 z-10 font-medium text-sm uppercase tracking-wider'>
                  Campaign Name
                </TableHead>
                <TableHead className='text-right font-medium text-sm uppercase tracking-wider'>
                  Sent
                </TableHead>
                <TableHead className='text-right font-medium text-sm uppercase tracking-wider'>
                  Delivered
                </TableHead>
                <TableHead className='font-medium text-sm uppercase tracking-wider'>
                  Message
                </TableHead>
                <TableHead className='text-right font-medium text-sm uppercase tracking-wider'>
                  Revenue
                </TableHead>
                <TableHead className='text-right font-medium text-sm uppercase tracking-wider'>
                  Opt-out Rate
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pushCampaigns.map(campaign => (
                <TableRow
                  key={campaign.id}
                  className='hover:bg-gray-50 cursor-pointer transition-colors text-md'
                >
                  <TableCell className='font-medium bg-white sticky left-0 z-10 flex items-center gap-2 !border-0'>
                    {campaign.name}
                    <ChevronRight className='h-4 w-4 text-muted-foreground opacity-50' />
                  </TableCell>
                  <TableCell className='text-right !border-0'>
                    {campaign.sent.toLocaleString()}
                  </TableCell>
                  <TableCell className='text-right !border-0'>
                    {campaign.delivered.toLocaleString()}
                  </TableCell>
                  <TableCell className='max-w-[200px] !border-0'>
                    <TooltipProvider>
                      <Tooltip delayDuration={300}>
                        <TooltipTrigger asChild>
                          <div className='truncate cursor-help hover:text-secondary transition-colors duration-200'>
                            {campaign.message}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent
                          className='max-w-sm p-3 text-md leading-relaxed whitespace-pre-wrap break-words'
                          side='top'
                          align='start'
                        >
                          <div className='font-medium mb-1'>Full Message:</div>
                          <div>{campaign.message}</div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  <TableCell className='text-right !border-0'>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        handleRevenueClick({
                          name: campaign.name,
                          revenue: campaign.revenue,
                        });
                      }}
                      className={`font-medium ${
                        campaign.revenue !== '$0'
                          ? 'text-secondary hover:text-secondary/80 hover:underline'
                          : 'text-muted-foreground'
                      }`}
                    >
                      {campaign.revenue}
                    </button>
                  </TableCell>
                  <TableCell className='text-right !border-0'>
                    {campaign.optOutRate}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
      <div className='absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-white to-transparent pointer-events-none z-20 hidden sm:block'></div>
      <div className='absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-l from-white to-transparent pointer-events-none z-20 hidden sm:block'></div>
      <div className='text-xs text-muted-foreground p-4 text-center sm:hidden'>
        Swipe horizontally to view all data →
      </div>

      {selectedCampaign && (
        <OrdersModal
          isOpen={!!selectedCampaign}
          onClose={() => setSelectedCampaign(null)}
          campaignName={selectedCampaign.name}
          revenue={selectedCampaign.revenue}
        />
      )}
    </div>
  );
}
